import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useAuth } from './AuthContext.tsx';
import screenerApi from '../services/screenerApi.ts';
import announcementsApi from '../services/announcementsApi.ts';

interface ScreenerUnreadContextType {
  counts: Record<string, number>;
  announcementCount: number;
  markSeen: (code: string) => void;
  markAnnouncementsSeen: () => void;
  refresh: () => void;
}

const ScreenerUnreadContext = createContext<ScreenerUnreadContextType>({
  counts: {},
  announcementCount: 0,
  markSeen: () => {},
  markAnnouncementsSeen: () => {},
  refresh: () => {},
});

export const ScreenerUnreadProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [announcementCount, setAnnouncementCount] = useState(0);

  const isFetchingRef = useRef(false); // prevent duplicate in-flight calls

  // ─── Single fetch function ────────────────────────────────────────────────
  const fetchCounts = useCallback(async () => {
    if (isFetchingRef.current) return; // already in-flight, skip
    isFetchingRef.current = true;
    try {
      const [screenerData, annCount] = await Promise.all([
        screenerApi.getUnreadCounts(),
        announcementsApi.getUnreadCount(),
      ]);
      setCounts(screenerData);
      setAnnouncementCount(annCount);
    } catch {
      // silently ignore — stale state is fine
    } finally {
      isFetchingRef.current = false;
    }
  }, []); // no deps — stable reference, reads auth state only through API token

  // ─── Start / stop polling based on auth state ─────────────────────────────
  useEffect(() => {
    if (!isAuthenticated || !user?.isPremium) {
      // Clear state on logout or downgrade
      setCounts({});
      setAnnouncementCount(0);
      return;
    }

    // Fetch once on login / premium status change
    fetchCounts();
  }, [isAuthenticated, user?.isPremium, fetchCounts]);

  // ─── Mark screener seen — optimistic state update, then sync backend ──────
  const markSeen = useCallback((code: string) => {
    if (!isAuthenticated || !user?.isPremium) return;
    if (!counts[code]) return; // count is 0 — no API call needed
    setCounts(prev => ({ ...prev, [code]: 0 }));
    screenerApi.markScreenerSeen(code).catch(() => {});
  }, [isAuthenticated, user?.isPremium, counts]);

  // ─── Mark announcements seen — same pattern ───────────────────────────────
  const markAnnouncementsSeen = useCallback(() => {
    if (!isAuthenticated || !user?.isPremium) return;
    if (announcementCount === 0) return; // count is 0 — no API call needed
    setAnnouncementCount(0);
    announcementsApi.markSeen().catch(() => {});
  }, [isAuthenticated, user?.isPremium, announcementCount]);

  // ─── Public refresh (manual trigger if needed) ────────────────────────────
  const refresh = useCallback(() => {
    fetchCounts();
  }, [fetchCounts]);

  return (
    <ScreenerUnreadContext.Provider value={{ counts, announcementCount, markSeen, markAnnouncementsSeen, refresh }}>
      {children}
    </ScreenerUnreadContext.Provider>
  );
};

export const useScreenerUnread = () => useContext(ScreenerUnreadContext);
